/**
 * @file: analytic-events.ts
 * @copyright: 2021, popshop live, inc.
 *
 * Analytic events functions in this file are generated by a tool from the
 * definition at https://github.com/popshoplive/psl-analytics-events.
 * Don't make changes to this file, otherwise all changes you made will be
 * overwritten the next time this file is automatically generated again.
 */

export type AnalyticEventProps = {
  [key: string]: any;
  [index: number]: any;
};

export type AnalyticEventListener = (
  event: string,
  props?: AnalyticEventProps,
) => void;

export interface AnalyticEventEmitter {
  emit(event: string, props?: AnalyticEventProps): void;
  addEventListener(listener: AnalyticEventListener): () => void;
  removeEventListener(listener: AnalyticEventListener): void;
}

/**
 * Class that provides methods for psl analytic event tracking.
 * @abstract
 *
 * @description
 * This class is abstract thus is not intended be used directly by creating
 * instance of this class and call event tracking methods on it.
 *
 * Instead, either use the singleton instance `AnalyticEvents.default` that
 * provides the default implementation in a event driven manner, for example:
 *
 * @example
 * ```typescript
 * // register event listener that handles emitted events
 * AnalyticEvents.default.addEventListener((event, props) => {
 *  // call other event library method to do the real event dispatch
 *  someEventLib.track(event, props);
 * });
 * // call instance methods on the default instance to emit events
 * AnalyticEvents.default.liveShowBroadcastStarted({
 *   showId: '...',
 *   agoraAccount: '...'
 * });
 * ```
 * Or, you can implement this class yourself by implementing the `track` method,
 * for example,
 *
 * @example
 * ```typescript
 * class MyAnalyticEventsImpl extends AnalyticEvents {
 *   track(event: string, props: AnalyticEventProps) {
 *     // call other event library method to do the real event dispatch
 *     someEventLib.track(event, props);
 *   }
 * }
 * // create instance of your implementation
 * const analyticEvents = new MyAnalyticEventsImpl();
 * // and then call instance event tracking methods on it
 * analyticEvents.liveShowBroadcastStarted({
 *   showId: '...',
 *   agoraAccount: '...'
 * });
 * ```
 * The default implementation is recommended to use unless custom implementation
 * is necessary in rare cases.
 */
export abstract class AnalyticEvents<T = unknown> {
  //#region Default Implementation
  /** @readonly instance that implements the default analytic event tracking */
  static readonly default = new (class DefaultAnalyticEventsImpl
    extends AnalyticEvents
    implements AnalyticEventEmitter
  {
    private listeners: AnalyticEventListener[] = [];

    /**
     * Method that can be called to emit custom analytic event that is not
     * exposed via the automatically generated tracking methods.
     * @param event the name of the custom event to emit
     * @param [props] the custom event properties
     */
    emit(event: string, props?: AnalyticEventProps): void {
      props = eventPropsConverted(props);
      for (const listener of this.listeners) {
        listener(event, props);
      }
    }

    /**
     * Add analytic event listener and implement actual event forward there.
     * @param listener the event listener to add
     * @returns a function that can be called to remove the listener
     */
    addEventListener(listener: AnalyticEventListener): () => void {
      this.listeners.push(listener);
      return () => {
        this.removeEventListener(listener);
      };
    }

    /**
     * Remove the given event listener.
     * @param listener the listener to remove
     */
    removeEventListener(listener: AnalyticEventListener): void {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    }

    // the default track method implementation
    protected track(event: string, props?: AnalyticEventProps) {
      this.emit(event, props);
    }
  })();
  //#endregion

  /**
   * Derived class should implement this method to track the given events
   * with properties.
   * @param {string} event - event name
   * @param {string} [props] - event properties
   */
  protected abstract track(event: string, props?: AnalyticEventProps): T;









  //#region Popstore Related Analytic Events

  /**
   * Analytic event logged on popstore bio link clicked
   * @param props - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioLinkClicked(
    props: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** button_type property */
      buttonType:
        | 'text'
        | 'image'
        | 'video'
        | 'digital-product'
        | 'calendar'
        | 'avatar-me'
        | 'chat'
        | 'follow'
        | 'group'
        | 'music'
        | 'tipping';
      /** button_heading property */
      buttonHeading?: string | null;
      /** button_sub_heading property */
      buttonSubHeading?: string | null;
      /** button_url property */
      buttonUrl?: string | null;
      /** button_text_alignment property */
      buttonTextAlignment?: string | null;
      /** button_text_vertical_alignment property */
      buttonTextVerticalAlignment?: string | null;
      /** button_text_color property */
      buttonTextColor?: string | null;
      /** button_sub_heading_color property */
      buttonSubHeadingColor?: string | null;
      /** button_border_color property */
      buttonBorderColor?: string | null;
      /** button_shape property */
      buttonShape?: string | null;
      /** button_is_hidden property */
      buttonIsHidden?: boolean | null;
      /** has_left_icon property */
      hasLeftIcon?: boolean | null;
      /** has_right_icon property */
      hasRightIcon?: boolean | null;
      /** button_animation property */
      buttonAnimation?: string | null;
      /** button_image_url property */
      buttonImageUrl?: string | null;
      /** button_video_url property */
      buttonVideoUrl?: string | null;
      /** button_product_id property */
      buttonProductId?: string | null;
      /** button_product_name property */
      buttonProductName?: string | null;
      /** button_product_price property */
      buttonProductPrice?: number | null;
      /** button_product_discount_price property */
      buttonProductDiscountPrice?: number | null;
      /** button_product_discount_price_enabled property */
      buttonProductDiscountPriceEnabled?: boolean | null;
      /** button_product_thumbnail property */
      buttonProductThumbnail?: string | null;
      /** button_overlay_shadow_opacity property */
      buttonOverlayShadowOpacity?: number | null;
      /** button_overlay_blur_effect property */
      buttonOverlayBlurEffect?: number | null;
      /** is_owner property */
      isOwner?: boolean | null;
      /** creator_id property */
      creatorId?: string | null;
      /** creator_url_code property */
      creatorUrlCode?: string | null;
      /** user_id property */
      userId?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_link_clicked', {
      elementAction: 'popstore_bio_link_clicked',
      elementActionStage: 'popstore_bio_link_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore bio message me clicked
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreBioMessageMeClicked(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_bio_message_me_clicked', {
      elementAction: 'popstore_bio_message_me_clicked',
      elementActionStage: 'popstore_bio_message_me_clicked',
      alsoSendToCsDatalake: true,
      elementCategory: 'profile',
      ...props,
      ...extra,
    });
  }

  /**
   * Analytic event logged on popstore my store meeting slot google calendar
   * connected
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreMyStoreMeetingSlotGoogleCalendarConnected(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** [Internal] whether to send this event to customer service datalake */
      alsoSendToCsDatalake?: boolean | null;
      /** if it is the first time the user connects their Google Calendar */
      isFirstConnection?: boolean | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track(
      'popstore_my_store_meeting_slot_google_calendar_connected',
      {
        elementAction:
          'popstore_my_store_meeting_slot_google_calendar_connected',
        elementActionStage:
          'popstore_my_store_meeting_slot_google_calendar_connected',
        alsoSendToCsDatalake: true,
        elementCategory: 'meetings',
        ...props,
        ...extra,
      },
    );
  }

  /**
   * Analytic event logged on popstore account deletion requested
   * @param [props] - event properties
   * @param [extra] - extra properties for this event
   */
  popstoreAccountDeletionRequested(
    props?: {
      /** cta_type property */
      ctaType?: ('button' | 'link' | 'menu_item') | null;
      /** cta_title property */
      ctaTitle?: string | null;
      /** cta_object_id property */
      ctaObjectId?: string | null;
      /** element_action_stage property */
      elementActionStage?: string | null;
      /** element_identifier property */
      elementIdentifier?: string | null;
      /** reason property */
      reason?: string | null;
    },
    extra?: AnalyticEventProps,
  ) {
    return this.track('popstore_account_deletion_requested', {
      elementAction: 'popstore_account_deletion_requested',
      elementActionStage: 'popstore_account_deletion_requested',
      ...props,
      ...extra,
    });
  }

  //#endregion
}

/**
 * Convert the given event props to the canonical form so that the property
 * keys all take the snake_cased form in order to keep them consistent with
 * their original definition in the schema configuration file.
 * @param [props] - the event props to convert
 * @returns converted props with canonical property keys
 */
export function eventPropsConverted(props: AnalyticEventProps | undefined) {
  if (!props) {
    return props;
  }
  const transformedProps: typeof props = {};
  for (const p in props) {
    const key = p
      .replace(
        /([^a-z])([a-z]*)/g,
        (_, l, t, i) =>
          (i ? '_' : '') + (l >= 'A' && l <= 'Z' ? l.toLowerCase() : l) + t,
      )
      .replace(/_{2,}/g, '_')
      .replace(/^_/, '')
      .replace(/_$/, '');
    transformedProps[key] = props[p];
  }
  return transformedProps;
}
